import React, { useState, useRef } from 'react';
import { Mic, Square, Loader, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_CONFIG, { getFullUrl } from '../config/apiConfig';
import ApiService from '../services/api';

const VoiceRecorder = ({ onSpeechRecognized, disabled, lang, className }) => {
  const { i18n } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const getActiveLanguage = () => {
    const activeLang = lang || i18n.language || 'mr';
    return activeLang.toLowerCase().startsWith('en') ? 'en' : 'mr';
  };

  const startRecording = async () => {
    setError(null);
    audioChunksRef.current = [];
    setRecordingTime(0);

    const activeLang = getActiveLanguage();

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Microphone access is not supported in this browser.");
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        await sendAudioToApi(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 60) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.error("VoiceRecorder error:", err);
      setError(
        activeLang === 'mr'
          ? "मायक्रोफोन प्रवेश नाकारला किंवा उपलब्ध नाही."
          : "Microphone access denied or unavailable."
      );
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
  };

  const sendAudioToApi = async (audioBlob) => {
    setIsProcessing(true);
    setError(null);

    const activeLang = getActiveLanguage();

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'speech.wav');
      formData.append('language', activeLang);

      // Endpoint selection based on language:
      // English -> /api/bhashini/speech-to-text1
      // Marathi -> /api/bhashini/speech-to-text
      const endpoint =
        activeLang === 'en'
          ? API_CONFIG.ENDPOINTS.BHASHINI_STT_EN || '/bhashini/speech-to-text1'
          : API_CONFIG.ENDPOINTS.BHASHINI_STT_MR || '/bhashini/speech-to-text';

      let data;
      try {
        data = await ApiService.uploadFile(endpoint, formData);
      } catch (serviceErr) {
        // Fallback to fetch using getFullUrl & auth token
        const url = getFullUrl(endpoint);
        const token =
          (ApiService.getAuthToken && ApiService.getAuthToken()) ||
          localStorage.getItem('token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Server returned status ${response.status}`);
        }

        data = await response.json();
      }

      const transcribedText =
        data?.text ||
        data?.transcribedText ||
        data?.transcript ||
        data?.result ||
        data?.source ||
        (typeof data?.data === 'string'
          ? data.data
          : data?.data?.text || data?.data?.transcribedText) ||
        data?.pipelineResponse?.[0]?.output?.[0]?.source ||
        data?.pipelineResponse?.[0]?.output?.[0]?.target ||
        (typeof data === 'string' ? data : '');

      if (transcribedText && transcribedText.trim()) {
        onSpeechRecognized(transcribedText.trim());
      } else {
        setError(
          activeLang === 'mr'
            ? "आवाज स्पष्ट ऐकू आला नाही. कृपया पुन्हा प्रयत्न करा."
            : "No text could be recognized from the speech. Please try again."
        );
      }
    } catch (err) {
      console.error("Speech-to-text API error:", err);
      setError(
        activeLang === 'mr'
          ? "स्पीच सेवा त्रुटी. कृपया पुन्हा प्रयत्न करा."
          : "Speech service error. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const activeLang = getActiveLanguage();

  return (
    <div className={`inline-flex flex-col items-end gap-1 ${className || ''}`}>
      <div className="flex items-center gap-2">
        {!isRecording && !isProcessing && (
          <button
            type="button"
            onClick={startRecording}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
            title="Voice Input"
          >
            <Mic className="w-3.5 h-3.5 text-blue-600" />
            <span>{activeLang === 'mr' ? "आवाज इनपुट" : "Voice Input"}</span>
          </button>
        )}

        {isRecording && (
          <button
            type="button"
            onClick={stopRecording}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors animate-pulse cursor-pointer shadow-sm"
          >
            <Square className="w-3.5 h-3.5 fill-current" />
            <span>
              {activeLang === 'mr' ? "थांबवा" : "Stop Recording"} ({formatTime(recordingTime)})
            </span>
          </button>
        )}

        {isProcessing && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg">
            <Loader className="w-3.5 h-3.5 text-blue-600 animate-spin" />
            <span>{activeLang === 'mr' ? "रुपांतरित होत आहे..." : "Processing..."}</span>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-1 text-[11px] text-red-600">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}


    </div>
  );
};

export default VoiceRecorder;

